import type { AuthTokens, User } from '@/types';

const ACCESS_TOKEN_KEY = 'sggadgets_access_token';
const REFRESH_TOKEN_KEY = 'sggadgets_refresh_token';
const TOKEN_EXPIRY_KEY = 'sggadgets_token_expires_at';
const USER_KEY = 'sggadgets_user';

export function getToken(): string | null {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!token || !expiresAt) return null;

  if (Date.now() >= Number(expiresAt)) {
    removeToken();
    return null;
  }

  return token;
}

export function setToken(tokens: AuthTokens): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  localStorage.setItem(TOKEN_EXPIRY_KEY, String(tokens.expiresAt));
}

export function removeToken(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(USER_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function isLoggedIn(): boolean {
  return getToken() !== null;
}

export function getUser(): User | null {
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;
  try {
    return JSON.parse(userStr) as User;
  } catch {
    return null;
  }
}

export function setUser(user: User): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function removeUser(): void {
  localStorage.removeItem(USER_KEY);
}

export async function refresh(): Promise<AuthTokens | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      removeToken();
      return null;
    }

    const tokens = (await response.json()) as AuthTokens;
    setToken(tokens);
    return tokens;
  } catch {
    removeToken();
    return null;
  }
}

export function getTokenExpiry(): number | null {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);
  return expiresAt ? Number(expiresAt) : null;
}

export function isTokenExpiringSoon(thresholdMs = 300000): boolean {
  const expiresAt = getTokenExpiry();
  if (!expiresAt) return true;
  return Date.now() + thresholdMs >= expiresAt;
}
